import React from 'react';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface YesNoToggleProps {
  label: React.ReactNode;
  value: boolean | null;
  onChange: (value: boolean | null) => void;
  /** Clicking the selected option clears it back to "unanswered" */
  clearable?: boolean;
  hint?: string;
}

/**
 * Single yes/no control used by every calculator. The bare check/X icons the
 * calculators used before gave no hint which side meant what, so both options
 * are labelled.
 */
const YesNoToggle: React.FC<YesNoToggleProps> = ({
  label,
  value,
  onChange,
  clearable = true,
  hint,
}) => {
  const { t } = useTranslation('hematology');

  const pick = (next: boolean) => {
    onChange(clearable && value === next ? null : next);
  };

  return (
    <div className="hema-input-group">
      <label className="hema-label">{label}</label>

      <div className="yesno" role="group">
        <button
          type="button"
          className={`yesno__btn yesno__btn--yes ${value === true ? 'is-active' : ''}`}
          aria-pressed={value === true}
          onClick={() => pick(true)}
        >
          <Check size={16} strokeWidth={2.5} />
          <span>{t('yes')}</span>
        </button>

        <button
          type="button"
          className={`yesno__btn yesno__btn--no ${value === false ? 'is-active' : ''}`}
          aria-pressed={value === false}
          onClick={() => pick(false)}
        >
          <X size={16} strokeWidth={2.5} />
          <span>{t('no')}</span>
        </button>
      </div>

      {hint && <p className="hema-hint">{hint}</p>}
    </div>
  );
};

export default YesNoToggle;
